import { useEffect, useRef } from "react";
import * as THREE from "three";
import type { ArchDefinition, ViewMode } from "../../types/dental";
import type { CrownSolveResult } from "../../types/restoration";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { buildCrownOverlayMesh } from "./viewport/restorationOverlay";
import { createViewportScene, disposeScene } from "./viewport/sceneBuilders";
import { buildArchGroup } from "./viewport/archBuilder";

interface CrownEditorViewportProps {
  upper: ArchDefinition;
  lower: ArchDefinition;
  viewMode: ViewMode;
  restoration: CrownSolveResult | null;
  contextToothIds?: string[];
}

export function CrownEditorViewport({
  upper,
  lower,
  viewMode,
  restoration,
  contextToothIds = [],
}: CrownEditorViewportProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const {
      scene,
      camera,
      renderer,
      anatomyGroup,
      restorationGroup,
    } = createViewportScene(host);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const contextToothSet = new Set(contextToothIds);
    const contextMeshes: THREE.Object3D[] = [];

    const collectContextTeeth = (group: THREE.Group) => {
      group.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        const toothId = mesh.userData?.toothId as string | undefined;
        if (!toothId || !contextToothSet.has(toothId)) {
          return;
        }

        const cloned = mesh.clone();
        cloned.position.copy(mesh.position);
        cloned.rotation.copy(mesh.rotation);
        cloned.scale.copy(mesh.scale);

        const sourceMaterial = Array.isArray(mesh.material)
          ? mesh.material[0]
          : mesh.material;

        const contextMaterial = new THREE.MeshStandardMaterial({
          color:
            sourceMaterial instanceof THREE.MeshStandardMaterial
              ? sourceMaterial.color.getHex()
              : 0x94a3b8,
          transparent: true,
          opacity: 0.28,
          metalness: 0.05,
          roughness: 0.75,
        });

        (cloned as THREE.Mesh).material = contextMaterial;
        contextMeshes.push(cloned);
      });
    };

    if (contextToothSet.size > 0) {
      if (viewMode === "upper" || viewMode === "both") {
        const upperBuilt = buildArchGroup(
          upper,
          0x2563eb,
          12,
          null,
          null,
          []
        );
        collectContextTeeth(upperBuilt.group);
      }

      if (viewMode === "lower" || viewMode === "both") {
        const lowerBuilt = buildArchGroup(
          lower,
          0x16a34a,
          0,
          null,
          null,
          []
        );
        collectContextTeeth(lowerBuilt.group);
      }

      for (const mesh of contextMeshes) {
        anatomyGroup.add(mesh);
      }
    }

    let focusTarget = new THREE.Vector3(0, 10, 20);

    if (restoration?.success) {
      const restorationMesh = buildCrownOverlayMesh(restoration);

      if (restorationMesh) {
        restorationMesh.userData = {
          ...restorationMesh.userData,
          type: "restoration",
          restorationType: restoration.metadata?.restorationType ?? "crown",
          planId: restoration.metadata?.planId,
        };

        restorationGroup.add(restorationMesh);
      }

      focusTarget = new THREE.Vector3(
        restoration.center[0],
        restoration.center[1],
        restoration.center[2]
      );
    }

    controls.target.copy(focusTarget);
    camera.position.set(
      focusTarget.x,
      focusTarget.y + 32,
      focusTarget.z + 70
    );
    camera.lookAt(focusTarget);
    controls.update();

    const onResize = () => {
      const nextWidth = host.clientWidth || 600;
      const nextHeight = host.clientHeight || 240;

      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(nextWidth, nextHeight);
    };

    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = window.requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();
    window.addEventListener("resize", onResize);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      renderer.dispose();

      if (renderer.domElement.parentElement === host) {
        host.removeChild(renderer.domElement);
      }

      disposeScene(scene);
    };
  }, [upper, lower, viewMode, restoration, contextToothIds]);

  return (
    <div className="card crown-editor-panel">
      <div className="crown-editor-panel__header">
        <div>
          <div className="panel-title">Restoration Editor</div>
          <div className="panel-muted">
            Isolated editing viewport for the selected restoration
          </div>
        </div>
      </div>

      <div ref={hostRef} className="dental-viewport-host dental-viewport-host--editor" />
    </div>
  );
}